/**
 * Publicação atómica da rota visível.
 *
 * Um `RouteDraft` privado recolhe agenda, pernas, refeições, orçamento e
 * marcadores durante a geração. Só depois de validado é publicado UMA vez
 * como `ItinerarySnapshot`. A rota visível nunca mostra estado intermédio.
 *
 * Módulo puro: sem IO, sem React, sem relógio.
 */

import {
  buildItinerarySnapshot,
  type ItinerarySnapshot,
  type SnapshotEntryInput,
} from "./itinerary-snapshot";
import { validateRouteDays, type RouteDayIssue } from "./route-days";
import type { DayWindow } from "./day-windows";
import {
  snapshotConsistencyPassed,
  type RouteBudget,
  type TravelLeg,
} from "./route-snapshot";

export type RouteDraft = {
  /** Identidade da geração que produziu este rascunho. */
  generationId: string;
  /** Revisão do modo de transporte (a pé → carro cria uma nova). */
  modeRevision: number;
  routeRevision: number;
  city: string;
  entries: SnapshotEntryInput[];
  travelLegs: TravelLeg[];
  budget: RouteBudget;
  /** Assinatura do hotel selecionado (ver `hotel-selection.ts`). */
  hotelSignature?: string;
  /** Janelas por dia: origem única da estrutura de dias publicada. */
  dayWindows?: DayWindow[];
};

export type PublicationContext = {
  generationId: string;
  modeRevision: number;
};

export type PublishResult =
  | { status: "published"; snapshot: ItinerarySnapshot }
  | { status: "unchanged"; snapshot: ItinerarySnapshot; reason: string }
  | { status: "stale" | "invalid"; snapshot: ItinerarySnapshot | null; reason: string };

/** Uma resposta assíncrona só é aplicável se ainda pertencer ao estado atual. */
export function isCurrentResponse(
  current: PublicationContext,
  incoming: Partial<PublicationContext>,
): boolean {
  if (incoming.generationId != null && incoming.generationId !== current.generationId) return false;
  if (incoming.modeRevision != null && incoming.modeRevision !== current.modeRevision) return false;
  return true;
}

/**
 * Problemas de estrutura de dias que impedem a publicação: dia duplicado, dia
 * em falta, entrada duplicada, entrada sem dia e entrada sem horas válidas.
 */
export function routeDayIssues(snapshot: ItinerarySnapshot): RouteDayIssue[] {
  return validateRouteDays(snapshot.days, snapshot.entries);
}

/** Invariantes cronológicos e de coerência antes de publicar. */
export function draftInvariantsPassed(snapshot: ItinerarySnapshot): boolean {
  const byDay = new Map<number, typeof snapshot.entries>();
  for (const e of snapshot.entries) {
    if (e.endAt.getTime() < e.startAt.getTime()) return false;
    const list = byDay.get(e.day) ?? [];
    list.push(e);
    byDay.set(e.day, list);
  }
  for (const list of byDay.values()) {
    const sorted = [...list].sort((a, b) => a.order - b.order);
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i].startAt.getTime() < sorted[i - 1].startAt.getTime()) return false;
    }
  }
  return snapshotConsistencyPassed(snapshot);
}

/**
 * Assinatura de conteúdo de um snapshot: locais, dias, horas, pernas e
 * orçamento. Dois snapshots com a mesma assinatura são a MESMA rota visível.
 */
export function snapshotContentSignature(snapshot: ItinerarySnapshot): string {
  return JSON.stringify({
    city: snapshot.city,
    hotel: snapshot.hotelSignature ?? "hotel:none",
    entries: snapshot.entries.map((e) => [
      e.logicalId ?? e.scheduleEntryId,
      e.day,
      e.startAt.getTime(),
      e.endAt.getTime(),
      e.kind === "stop" ? `stop:${e.name}` : `break:${e.meal}:${e.venue?.placeId ?? e.venue?.name ?? ""}`,
      // Dados visuais publicados: uma foto ou local de refeição tardio é uma
      // nova revisão visível, não uma mutação do cartão já publicado.
      JSON.stringify(snapshot.entryDataById[e.scheduleEntryId] ?? null),
    ]),
    legs: snapshot.travelLegs.map((l) => [l.legId, l.resolvedMode, l.status, l.distanceMeters, l.durationMinutes]),
    budget: snapshot.budget,
  });
}

/**
 * Publica o rascunho como snapshot canónico. Em resposta antiga ou rascunho
 * inválido devolve o snapshot anterior intacto — nunca uma mistura parcial.
 * Um rascunho com conteúdo idêntico ao publicado devolve `unchanged`: publicar
 * de novo criaria um ciclo de render sem qualquer alteração visível.
 */
export function publishRouteDraft(
  published: ItinerarySnapshot | null,
  draft: RouteDraft,
  context: PublicationContext,
): PublishResult {
  if (!isCurrentResponse(context, draft)) {
    return { status: "stale", snapshot: published, reason: "stale_response" };
  }
  if (published && draft.routeRevision < published.routeRevision) {
    return { status: "stale", snapshot: published, reason: "old_revision" };
  }
  const snapshot = buildItinerarySnapshot({
    routeRevision: draft.routeRevision,
    generationRequestId: draft.generationId,
    city: draft.city,
    entries: draft.entries,
    travelLegs: draft.travelLegs,
    budget: draft.budget,
    ...(draft.dayWindows ? { dayWindows: draft.dayWindows } : {}),
    ...(draft.hotelSignature ? { hotelSignature: draft.hotelSignature } : {}),
  });
  if (!draftInvariantsPassed(snapshot)) {
    return { status: "invalid", snapshot: published, reason: "invariants_failed" };
  }
  // Uma visita obrigatória sem horas nunca desaparece em silêncio: a
  // publicação falha e a rota visível anterior mantém-se intacta.
  if (snapshot.droppedRequired.length) {
    return {
      status: "invalid",
      snapshot: published,
      reason: `required_without_times:${snapshot.droppedRequired.join(",")}`,
    };
  }
  const dayIssues = routeDayIssues(snapshot);
  if (dayIssues.length) {
    return { status: "invalid", snapshot: published, reason: `days_invalid:${dayIssues[0].code}` };
  }
  if (published && snapshotContentSignature(published) === snapshotContentSignature(snapshot)) {
    return { status: "unchanged", snapshot: published, reason: "identical_content" };
  }
  return { status: "published", snapshot };
}

/**
 * Nova revisão por mudança de modo: mesmos IDs, locais, refeições e hotel —
 * apenas pernas e horas são recalculadas.
 */
export function reviseDraftForMode(
  draft: RouteDraft,
  next: { modeRevision: number; entries: SnapshotEntryInput[]; travelLegs: TravelLeg[] },
): RouteDraft {
  return {
    ...draft,
    modeRevision: next.modeRevision,
    routeRevision: draft.routeRevision + 1,
    entries: next.entries,
    travelLegs: next.travelLegs,
  };
}

/** Identidade dos locais do rascunho — prova que a troca de modo não os alterou. */
export function draftPlaceIdentity(draft: RouteDraft | ItinerarySnapshot): string[] {
  return draft.entries.map((e) =>
    e.kind === "stop" ? `stop:${e.name}` : `break:${e.meal}:${e.venue?.placeId ?? e.venue?.name ?? ""}`,
  );
}
